import React from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import { snakeCase } from 'lodash';
import * as Yup from 'yup';

import InstructionsMarkdownEditorFormik from '~/components/Admin/AiExperts/InstructionsMarkdownEditorFormik';
import CardDialog from '~/components/CardDialog';
import DialogFooterActionsUntyped from '~/components/core/DialogFooterActions';
import Text from '~/components/core/TextComponents/Text';
import {
  useFormikValidationErrorToast,
  ValidationErrorToastContainer,
} from '~/components/hooks/useFormikValidationErrorToast';
import TextFieldFormik, { useSetDefaultFieldsOnChange } from '~/components/TextFieldFormik';

import TEXTS from './texts';
import type { PredefinedPromptFormValues, PredefinedPromptRow } from './types';

const TOAST_ID = 'predefined-prompt-errors';
const PROMPT_KEY_PATTERN = /^[a-z0-9_]+$/;

interface DialogFooterActionsProps {
  disabled?: boolean;
  onClickPrimary?: () => void;
  onClickSecondary?: () => void;
}
const DialogFooterActions = DialogFooterActionsUntyped as React.ComponentType<DialogFooterActionsProps>;

interface PredefinedPromptDialogProps {
  editedPrompt: PredefinedPromptRow | null;
  existingKeys: string[];
  isSaving: boolean;
  onSubmit: (values: PredefinedPromptFormValues) => void;
  onClose: () => void;
}

const buildValidationSchema = (existingKeys: string[], isEdit: boolean) =>
  Yup.object().shape({
    prompt_key: isEdit
      ? Yup.string()
      : Yup.string()
          .required(TEXTS.validation.keyRequired)
          .matches(PROMPT_KEY_PATTERN, TEXTS.validation.keyFormat)
          .notOneOf(existingKeys, TEXTS.validation.keyDuplicate),
    prompt_title: Yup.string().trim().required(TEXTS.validation.titleRequired),
    prompt_text: Yup.string().trim().required(TEXTS.validation.textRequired),
  });

const DialogFields: React.FC<{ isEdit: boolean }> = ({ isEdit }) => {
  useFormikValidationErrorToast({ toastId: TOAST_ID, title: TEXTS.validation.toastTitle });
  const { values } = useFormikContext<PredefinedPromptFormValues>();
  // A new prompt's key is always derived from its title; an existing prompt keeps its key.
  useSetDefaultFieldsOnChange(values.prompt_title, { prompt_key: snakeCase(values.prompt_title) }, null, isEdit, true);

  return (
    <Form className="flex flex-col gap-16">
      <ValidationErrorToastContainer containerId={TOAST_ID} />
      <TextFieldFormik id="prompt_title" label={TEXTS.fields.title} fullWidth />
      <TextFieldFormik
        id="prompt_key"
        label={TEXTS.fields.key}
        helperText={TEXTS.fields.keyHelper}
        disabled
        fullWidth
      />
      <div className="flex flex-col gap-8">
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
          {TEXTS.fields.text}
        </Text>
        <InstructionsMarkdownEditorFormik id="prompt_text" />
      </div>
    </Form>
  );
};

const PredefinedPromptDialog: React.FC<PredefinedPromptDialogProps> = ({
  editedPrompt,
  existingKeys,
  isSaving,
  onSubmit,
  onClose,
}) => {
  const isEdit = editedPrompt !== null;

  return (
    <Formik<PredefinedPromptFormValues>
      initialValues={{
        prompt_key: editedPrompt?.prompt_key ?? '',
        prompt_title: editedPrompt?.prompt_title ?? '',
        prompt_text: editedPrompt?.prompt_text ?? '',
      }}
      validationSchema={buildValidationSchema(existingKeys, isEdit)}
      onSubmit={(values) => {
        if (isSaving) return;
        onSubmit(values);
      }}
    >
      {({ submitForm }) => (
        <CardDialog
          isDialog
          open
          fullWidth
          maxWidth="md"
          title={isEdit ? TEXTS.editPrompt : TEXTS.createPrompt}
          onClose={onClose}
          preventClose={isSaving}
          footerActions={
            <DialogFooterActions onClickPrimary={submitForm} onClickSecondary={onClose} disabled={isSaving} />
          }
        >
          <DialogFields isEdit={isEdit} />
        </CardDialog>
      )}
    </Formik>
  );
};

export default PredefinedPromptDialog;
